import { Grid, Button } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import QTDetailsImageWithLabelSection from "../../../../../common/QTDetailsImageWithLabelSection";
import QTDetailsAudioWithLabelSection from "../../../../../common/QTDetailsAudioWithLabelSection";
import QTDetailsTextFieldWithLabelSection from "../../../../../common/QTDetailsTextFieldWithLabelSection";
import { capitalizeFirstLetter, images } from "../../../../../constants";
import { useContributorsContext } from "../../../../../providers/OrganizationAdmin/ContributorsProvider";
import CreatedByPickerModal from "../../../GAQuickTales/components/CreatedByPickerModal/CreatedByPickerModal";

const GAReactionDetailsDataFieldsSection = ({
  reactionDetails,
  setReactionDetails,
  handleImagePreviewModalOpen,
  handleAudioModalOpen,
  handleProcessedAudioModalOpen,
  editEnabled,
  uploadImage,
  setUploadImage,
  uploadAudioFile,
  setUploadAudioFile,
  uploadProcessedAudioFile,
  setUploadProcessedAudioFile,
  handleFileUpload,
  onProcessOriginalAudio,
}) => {
  const { GAUsers, handleGetAllGAUsers } = useContributorsContext();
  const [userPickerOpen, setUserPickerOpen] = useState(false);

  // Ensure the users list is loaded (used to populate the picker).
  useEffect(() => {
    if (!GAUsers || GAUsers.length === 0) {
      handleGetAllGAUsers();
    }
  }, []);

  // Filtered/sorted users list for the picker (mirrors the QuickTale picker).
  const usersData = useMemo(() => {
    let filtered = (GAUsers || []).filter((user) => user);
    filtered = filtered.filter((user) => {
      const fullName = `${user.firstName || ""} ${user.lastName || ""}`.trim();
      return fullName !== "Guest User";
    });
    filtered.sort((a, b) => {
      const nameA = `${a.firstName || ""} ${a.lastName || ""}`.toLowerCase().trim();
      const nameB = `${b.firstName || ""} ${b.lastName || ""}`.toLowerCase().trim();
      return nameA.localeCompare(nameB);
    });
    return filtered;
  }, [GAUsers]);

  // Human-readable label for the currently selected user (or empty).
  const getUserLabel = (user) => {
    if (!user) return "";
    const displayName = `${user.firstName || ""} ${user.lastName || ""}`.trim();
    if (user.userName) {
      return displayName ? `${displayName} /@${user.userName}` : `/@${user.userName}`;
    }
    return displayName || user.userName || "";
  };

  const currentUserLabel =
    getUserLabel(reactionDetails?.userData) || reactionDetails?.user || "";

  const handleUserSelect = (item) => {
    setReactionDetails({
      ...reactionDetails,
      user: item.id,
      userData: item,
    });
  };

  return (

    <Grid container style={dataContainerStyles}>
      {/*left side column*/}
      <Grid item container direction={"column"} xs>
        {/*photo section*/}
        <QTDetailsImageWithLabelSection
          label={"QT Photo"}
          image={
            reactionDetails?.quicktaleData?.imageUrls &&
            reactionDetails?.quicktaleData?.imageUrls[0]
              ? reactionDetails?.quicktaleData?.imageUrls[0]
              : images.QuicktaleLogo
          }
          handleImagePreviewModalOpen={handleImagePreviewModalOpen}
          details={reactionDetails}
          setDetails={setReactionDetails}
          uploadImage={uploadImage}
          setUploadImage={setUploadImage}
        />
        {/*audio section*/}
        <QTDetailsAudioWithLabelSection
          label={`Reaction Audio File${!reactionDetails.processedAudioUrl ? '*' : ''}`}
          handleAudioModalOpen={handleAudioModalOpen}
          editEnabled={editEnabled}
          details={reactionDetails}
          setDetails={setReactionDetails}
          uploadAudioFile={uploadAudioFile}
          setUploadAudioFile={setUploadAudioFile}
          handleFileUpload={(file) => handleFileUpload(file, "original")}
          showProcessButton={true}
          onProcessAudio={onProcessOriginalAudio}
        />
        {/*processed audio section*/}
        <QTDetailsAudioWithLabelSection
          label={`Processed Audio File${reactionDetails.processedAudioUrl ? '*' : ''}`}
          handleAudioModalOpen={handleProcessedAudioModalOpen}
          editEnabled={editEnabled}
          details={reactionDetails}
          setDetails={setReactionDetails}
          uploadAudioFile={uploadProcessedAudioFile}
          setUploadAudioFile={setUploadProcessedAudioFile}
          audioNameField="processedAudioName"
          audioUrlField="processedAudioUrl"
          hideUploadButton={true}
        />
        {/*reaction id section*/}
        <QTDetailsTextFieldWithLabelSection
          label={"Reaction ID"}
          value={reactionDetails?.id}
          disabled={true}
        />
        {/*QT title section*/}
        <QTDetailsTextFieldWithLabelSection
          label={"QT Title"}
          value={reactionDetails?.quicktaleData?.title}
          disabled={true}
        />
        {/*QT Description section*/}
        <QTDetailsTextFieldWithLabelSection
          // label={"Description"}
          label={"Admin Notes"}
          value={reactionDetails?.adminNotes}
          disabled={true}
          editEnabled={editEnabled}
          multiline={true}
          rows={4}
          onChange={(e) =>
            setReactionDetails({
              ...reactionDetails,
              adminNotes: e.target.value,
            })
          }
        />
      </Grid>
      {/*right side column*/}
      <Grid item container direction={"column"} xs>
        {/*Created By section
        <QTDetailsTextFieldWithLabelSection
          label={"Created By"}
          value={
            reactionDetails?.userData?.userName ||
            reactionDetails?.displayName ||
            "Guest"
            
          }
          disabled={true}
          // editEnabled={editEnabled}
        />*/}
        {/*User section — read-only display with a "Change" button (edit mode)
            that opens a Users picker. The stored value is the users.id.*/}
        <Grid container alignItems={"center"} style={{ marginBottom: 24 }}>
          <span style={userLabelStyle}>Created By</span>
          <div style={userRowStyle}>
            <div style={userValueStyle}>{currentUserLabel}</div>
            {editEnabled && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => setUserPickerOpen(true)}
                sx={{
                  textTransform: "none",
                  fontFamily: "Avenir Book, sans-serif",
                  borderColor: "#00A7CD",
                  color: "#00A7CD",
                  ml: 1,
                }}
              >
                Change
              </Button>
            )}
          </div>
        </Grid>
        {/*Display Name section - editable so admins can set/update the guest display name*/}
        <QTDetailsTextFieldWithLabelSection
          label={"Display Name"}
          value={reactionDetails?.displayName || ""}
          disabled={true}
          editEnabled={editEnabled}
          onChange={(e) =>
            setReactionDetails({
              ...reactionDetails,
              displayName: e.target.value,
            })
          }
        />


        <QTDetailsTextFieldWithLabelSection
          label={"Reaction Status"}
          value={reactionDetails?.status && reactionDetails?.status}
          disabled={true}
          editEnabled={editEnabled}
          select={true}
          dropdownValues={["pending", "approved", "denied", "removed"]}
          onChange={(e) =>
            setReactionDetails({
              ...reactionDetails,
              status: e.target.value,
            })
          }
        />
        <QTDetailsTextFieldWithLabelSection
          label={"Reported Status"}
          value={capitalizeFirstLetter(reactionDetails?.reportedStatus)}
          disabled={true}
        />
        <QTDetailsTextFieldWithLabelSection
          label={"Creation Date"}
          value={
            reactionDetails?.createdAt &&
            moment
              .unix(reactionDetails?.createdAt?.seconds)
              .format("MM-DD-YYYY HH:mm:ss")
          }
          disabled={true}
        />
      </Grid>

      {/*Users picker modal (Organizations tab intentionally left empty since a
          reaction's `user` is always a users.id).*/}
      <CreatedByPickerModal
        open={userPickerOpen}
        onClose={() => setUserPickerOpen(false)}
        usersData={usersData}
        organizationsData={[]}
        onSelect={handleUserSelect}
      />
    </Grid>
  );
};

export default GAReactionDetailsDataFieldsSection;

const dataContainerStyles = {
  borderRadius: 12,
  boxShadow: " 0px 0px 30px 0px rgba(80, 85, 136, 0.15)",
  background: "var(--gray-99, #FCFCFC)",
  padding: "36px 15px",
  marginBottom: 30,
};

const userLabelStyle = {
  width: "25%",
  color: "#020303",
  fontSize: 14,
  fontWeight: 700,
  fontFamily: "Avenir Book, sans-serif",
};

const userRowStyle = {
  width: "70%",
  display: "flex",
  alignItems: "center",
};

const userValueStyle = {
  flex: 1,
  color: "#515563",
  fontSize: 14,
  fontWeight: 500,
  fontFamily: "Avenir Book, sans-serif",
};


